import { useState, useEffect } from 'react'

import { Id } from '@/types/index'

type Favorites = Id[]

const useFavorite = (id?: Id) => {
  const [favoriteList, setFavoriteList] = useState<Favorites>([])
  const [isFavorite, setIsFavorite] = useState(false)

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'favorites') {
        try {
          const newFavorites = convertToValidIds(
            JSON.parse(event.newValue || '[]'),
          )
          setFavoriteList(newFavorites)
          setIsFavorite(id ? newFavorites.includes(id) : false)
        } catch (e) {
          console.warn('Invalid favorites format in storage:', e)
          setFavoriteList([])
          setIsFavorite(false)
        }
      }
    }

    const handleFavoritesUpdate = (event: CustomEvent<Favorites>) => {
      setFavoriteList(event.detail)
      setIsFavorite(id ? event.detail.includes(id) : false)
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener(
      'favoritesUpdated',
      handleFavoritesUpdate as EventListener,
    )

    getFavorite() // Initial load

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener(
        'favoritesUpdated',
        handleFavoritesUpdate as EventListener,
      )
    }
  }, [id])

  const getFavorite = () => {
    try {
      const favorites = new Set<Id>(
        JSON.parse(localStorage.getItem('favorites') || '[]'),
      )

      const favoritesArray = convertToValidIds(Array.from(favorites))
      saveInLocalStorage(favoritesArray)
      setFavoriteList(favoritesArray)
      setIsFavorite(id ? favoritesArray.includes(id) : false)

      return favorites
    } catch (e) {
      console.warn('Invalid favorites format in localStorage:', e)
      setFavoriteList([])
      setIsFavorite(false)
      return new Set<Id>()
    }
  }

  const convertToValidIds = (values: Favorites): number[] => {
    return Array.from(
      new Set(
        values
          .map((value) => {
            if (typeof value === 'string' && !isNaN(Number(value))) {
              return parseInt(value)
            } else if (typeof value === 'number') {
              return value
            }
            return null
          })
          .filter((value): value is number => value !== null),
      ),
    )
  }

  const updateFavorites = (id: Id, shouldAdd: boolean) => {
    const favorites = new Set<Id>(favoriteList)

    if (shouldAdd) {
      favorites.add(id)
    } else {
      favorites.delete(id)
    }

    const favoritesArray = [...favorites]
    saveInLocalStorage(favoritesArray)
    window.dispatchEvent(
      new CustomEvent('favoritesUpdated', { detail: favoritesArray }),
    )
  }

  const filterFavoritesAndRemoveDiff = (
    favorites: Favorites,
    ids: number[],
  ) => {
    const filteredFavorites = favorites.filter((favorite) =>
      ids.includes(favorite),
    )

    if (
      favorites.length !== filteredFavorites.length ||
      JSON.stringify([...favorites].sort()) !==
        JSON.stringify([...filteredFavorites].sort())
    ) {
      saveInLocalStorage(filteredFavorites)
      window.dispatchEvent(
        new CustomEvent('favoritesUpdated', { detail: filteredFavorites }),
      )
    }

    return filteredFavorites
  }

  const toggleFavorite = (newState: boolean) => {
    setIsFavorite(newState)
    if (id) {
      updateFavorites(id, newState)
    }
  }

  const saveInLocalStorage = (favorites: Favorites) => {
    if (
      JSON.stringify([...favorites].sort()) !==
      JSON.stringify([...favoriteList].sort())
    ) {
      localStorage.setItem(
        'favorites',
        JSON.stringify(convertToValidIds(favorites)),
      )
    }
  }

  const countFavorites = () => {
    return favoriteList.length
  }

  return {
    isFavorite,
    toggleFavorite,
    countFavorites,
    favoriteList,
    filterFavoritesAndRemoveDiff,
  }
}

export default useFavorite
